<template>
  <div class="w-full max-w-[452px] mx-auto lg:mx-0 lg:justify-self-end">
    <div class="leaderboard-card w-full max-w-[452px] bg-base-100/90 backdrop-blur-sm rounded-xl p-5 shadow-2xl text-base-content">
      <!-- Competition header -->
      <div class="mb-4">
        <div class="flex items-center justify-between gap-2 mb-4">
          <h2 class="text-xl font-bold leading-tight">{{ competition.name }}</h2>
          <span class="badge badge-success badge-outline gap-1.5 px-2 py-1.5 items-center shrink-0">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping [animation-duration:2.4s] absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-success"></span>
            </span>
            <span class="text-[10px] font-semibold uppercase tracking-wide">Live</span>
          </span>
        </div>

        <div class="flex flex-wrap gap-1.5 mb-3">
          <span class="badge badge-ghost gap-1 text-xs px-2">
            <i class="mdi mdi-trending-up text-sm"></i>
            {{ competition.direction }}
          </span>
          <span class="badge badge-ghost gap-1 text-xs px-2">
            <i class="mdi mdi-flag-outline text-sm"></i>
            {{ competition.universe }}
          </span>
          <span class="badge badge-ghost gap-1 text-xs px-2">
            <i class="mdi mdi-chart-pie-outline text-sm"></i>
            Max {{ competition.max_stocks }}
          </span>
          <span class="badge badge-ghost gap-1 text-xs px-2">
            <i class="mdi mdi-calendar-week-outline text-sm"></i>
            {{ Math.round(competition.duration_days / 7) }} weeks
          </span>
        </div>

        <div class="flex items-center gap-2">
          <progress
            class="progress progress-primary flex-1 h-1.5"
            :value="competition.days_elapsed"
            :max="competition.duration_days"
          ></progress>
          <span class="text-xs text-base-content/40 tabular-nums shrink-0">
            Day {{ competition.days_elapsed }} / {{ competition.duration_days }}
          </span>
        </div>
      </div>

      <div class="border-t border-dashed border-base-content/15 mb-4"></div>

      <div class="space-y-2">
        <TransitionGroup name="leaderboard-swap" tag="div" class="space-y-2">
          <div
            v-for="(entry, i) in topThree"
            :key="entry.display_name"
            class="leaderboard-row flex items-center gap-2 h-[60px]"
          >
            <MoverBadge :change="entry.position_change" />
            <div class="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg h-full" :class="rankBg(i)">
              <div class="w-5 shrink-0 text-center">
                <i v-if="i === 0" class="mdi mdi-trophy text-warning text-base"></i>
                <span v-else class="text-sm font-bold text-base-content/50">{{ i + 1 }}</span>
              </div>
              <span class="flex-1 font-semibold text-sm truncate text-left">{{ entry.display_name }}</span>
              <span class="w-12 text-right font-bold text-sm shrink-0" :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
                <FlipValue :display="formatReturn(entry.total_return)" :value-key="entry.total_return" />
              </span>
            </div>
            <div class="shrink-0 w-[60px] h-[60px] rounded-lg">
              <component :is="miniPortfolioChart" :stocks="entry.stocks" fill />
            </div>
          </div>
        </TransitionGroup>

        <div class="flex items-center gap-2 px-2 py-1">
          <div class="flex-1 border-t border-dashed border-base-content/20"></div>
          <span class="text-xs text-base-content/30">{{ gapCount }} more players</span>
          <div class="flex-1 border-t border-dashed border-base-content/20"></div>
        </div>

        <div class="flex items-center gap-2 h-[60px]">
          <MoverBadge :change="lastEntry.position_change" />
          <div class="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg h-full">
            <span class="w-5 shrink-0 text-center text-sm font-bold text-base-content/40">{{ competition.total_players }}</span>
            <span class="flex-1 font-semibold text-sm truncate text-left">{{ lastEntry.display_name }}</span>
            <span class="w-12 text-right font-bold text-sm shrink-0" :class="lastEntry.total_return >= 0 ? 'text-success' : 'text-error'">
              <FlipValue :display="formatReturn(lastEntry.total_return)" :value-key="lastEntry.total_return" />
            </span>
          </div>
          <div class="shrink-0 w-[60px] h-[60px] rounded-lg">
            <component :is="miniPortfolioChart" :stocks="lastEntry.stocks" fill />
          </div>
        </div>
      </div>

      <div class="mt-4">
        <router-link to="/competition" class="btn btn-primary w-full">
          View Competition
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import competition from '../data/competition.json'
import { createLiveDemoSteps } from '../data/liveDemoSteps.js'
import MiniPortfolioTreemap from './MiniPortfolioTreemap.vue'
import FlipValue from './FlipValue.vue'
import MoverBadge from './MoverBadge.vue'

const gapCount = competition.total_players - 4
const miniPortfolioChart = MiniPortfolioTreemap

const DEMO_STEP_INTERVAL_MS = 4000
const simulationStepIndex = ref(0)
const simulationDirection = ref(1)
let simulationTimer = null

const demoSteps = createLiveDemoSteps(competition)

const activeDemoStep = computed(() => demoSteps[simulationStepIndex.value] ?? demoSteps[0])
const previousStepIndex = computed(() => simulationStepIndex.value - simulationDirection.value)
const previousDemoStep = computed(() => {
  const idx = previousStepIndex.value
  return idx >= 0 && idx < demoSteps.length ? demoSteps[idx] : null
})
const rankMapForStep = (step) => {
  if (!step) return new Map()
  const map = new Map()
  step.topThree.forEach((entry) => map.set(entry.display_name, entry.rank))
  map.set(step.lastEntry.display_name, step.lastEntry.rank)
  return map
}
const computeStepChange = (entry, previousRanks) => {
  const previousRank = previousRanks.get(entry.display_name)
  if (typeof previousRank !== 'number') return 0
  return previousRank - entry.rank
}
const topThree = computed(() => {
  const previousRanks = rankMapForStep(previousDemoStep.value)
  return [...activeDemoStep.value.topThree]
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => ({
      ...entry,
      position_change: computeStepChange(entry, previousRanks),
    }))
})
const lastEntry = computed(() => {
  const previousRanks = rankMapForStep(previousDemoStep.value)
  const entry = activeDemoStep.value.lastEntry
  return {
    ...entry,
    position_change: computeStepChange(entry, previousRanks),
  }
})
const formatReturn = (value) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`

const advanceSimulationStep = () => {
  const maxStep = demoSteps.length - 1
  if (maxStep <= 0) return

  if (simulationStepIndex.value >= maxStep) {
    simulationDirection.value = -1
  } else if (simulationStepIndex.value <= 0) {
    simulationDirection.value = 1
  }
  simulationStepIndex.value += simulationDirection.value
}

onMounted(() => {
  if (demoSteps.length <= 1) return
  simulationTimer = window.setInterval(advanceSimulationStep, DEMO_STEP_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (simulationTimer) {
    window.clearInterval(simulationTimer)
    simulationTimer = null
  }
})

const rankBg = (i) => {
  if (i === 0) return 'bg-yellow-400/15 border border-yellow-400/30'
  if (i === 1) return 'bg-gray-300/10 border border-gray-400/20'
  if (i === 2) return 'bg-orange-400/10 border border-orange-400/20'
  return 'bg-base-200/50'
}
</script>

<style scoped>
.leaderboard-card {
  animation: float 6s ease-in-out infinite;
}

.leaderboard-row {
  will-change: transform;
}

.leaderboard-swap-move {
  transition: transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
}

.leaderboard-swap-enter-active,
.leaderboard-swap-leave-active {
  transition: opacity 260ms ease, transform 260ms ease;
}

.leaderboard-swap-enter-from,
.leaderboard-swap-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
</style>
